"use client";

import { useRef, forwardRef, useImperativeHandle } from "react";
import { Upload, X } from "lucide-react";

interface LogoUploadProps {
  logoPreview: string | null;
  onLogoChange: (files: FileList | null) => void;
  onLogoRemove: () => void;
  error?: string;
}

export interface LogoUploadRef {
  clearInput: () => void;
}

export const LogoUpload = forwardRef<LogoUploadRef, LogoUploadProps>(
  ({ logoPreview, onLogoChange, onLogoRemove, error }, ref) => {
    const fileInputRef = useRef<HTMLInputElement>(null);

    useImperativeHandle(ref, () => ({
      clearInput: () => {
        if (fileInputRef.current) {
          fileInputRef.current.value = "";
        }
      },
    }));

    return (
      <>
        <input
          ref={fileInputRef}
          id="logo"
          type="file"
          accept="image/*"
          className="hidden"
          onChange={(e) => onLogoChange(e.target.files)}
        />

        {!logoPreview ? (
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            className="w-full h-40 border-2 border-dashed border-muted-foreground/25 rounded-lg hover:border-muted-foreground/50 hover:bg-muted/50 transition-colors flex flex-col items-center justify-center gap-2 text-muted-foreground"
          >
            <Upload className="w-8 h-8" />
            <span className="text-sm font-medium">Click to upload logo</span>
            <span className="text-xs">PNG, JPG, GIF up to 10MB</span>
          </button>
        ) : (
          <div className="relative w-full h-40 border-2 border-border rounded-lg overflow-hidden group">
            <img
              src={logoPreview}
              alt="Logo preview"
              className="w-full h-full object-contain bg-muted"
            />
            <button
              type="button"
              onClick={onLogoRemove}
              className="absolute top-2 right-2 p-1.5 bg-destructive text-destructive-foreground rounded-full hover:bg-destructive/90 transition-colors opacity-0 group-hover:opacity-100"
            >
              <X className="w-4 h-4" />
            </button>
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              className="absolute bottom-2 right-2 px-3 py-1.5 bg-secondary text-secondary-foreground rounded-md hover:bg-secondary/80 transition-colors text-sm opacity-0 group-hover:opacity-100"
            >
              Change Logo
            </button>
          </div>
        )}

        {error && <p className="text-sm text-destructive">{error}</p>}
      </>
    );
  }
);

LogoUpload.displayName = "LogoUpload";
